import _ from 'lodash';
import type { OrderDTO, OrderProduct } from '../interfaces';
import type { OrderFormValues } from '../order.schema';
import OrderStatuses from '../../../enum/OrderStatuses';
import PaymentConfigEnum from '../../../enum/PaymentConfig';
import { STORE_RECEPTION_VALIDATION_UPDATE_MODE } from '../../../utils/storeReceptionValidation';
import { normalizeOrderScheduleTimeValue } from '../../../utils/ordersUtils';
import {
  normalizeAttributesForLineType,
  normalizeProductIdentity,
  normalizeSelectedOptionIdentity,
  resolveLineId
} from './lineIdentity';

export type CreateOrderPayload = Omit<
  OrderFormValues,
  'deliveryInfo' | 'pickupInfo' | 'products' | 'buyAndGetProducts'
> & {
  createdDate: string;
  products: Array<Record<string, unknown>>;
  buyAndGetProducts: Array<Record<string, unknown> & { isFreeItem?: boolean }>;
  deliveryInfo: { date: string; fromTime: string; toTime: string };
  pickupInfo?: { date: string; fromTime: string; toTime: string };
  status: string;
  amountPaid?: number;
};

/**
 * Returns top-level keys where obj1 and obj2 differ (same semantics as OrderSummary's local differencesKeys).
 */
export function getOrderUpdateChangedKeys(
  obj1: Record<string, unknown>,
  obj2: Record<string, unknown>
): string[] {
  return Object.keys(
    _.reduce(
      obj1,
      (result: Record<string, unknown>, value: unknown, key: string) => {
        if (!_.isEqual(value, obj2[key])) {
          result[key] =
            _.isObject(value) && _.isObject(obj2[key])
              ? _.differenceWith(
                  _.keys(value as object),
                  _.keys(obj2[key] as object),
                  _.isEqual
                )
              : value;
        }
        return result;
      },
      {}
    )
  );
}

function getLineProductId(line: Record<string, unknown>): string {
  return normalizeProductIdentity(line);
}

function getLineOptionId(line: Record<string, unknown>): string | null {
  return normalizeSelectedOptionIdentity(line);
}

/**
 * Maps a form/order line to the API payload shape for products/buyAndGetProducts.
 */
export function mapLineToPayload(line: Record<string, unknown>): Record<string, unknown> {
  const lineId =
    (line.lineId as string) ??
    (line.orderProductLineID as string) ??
    resolveLineId(line as unknown as OrderProduct);
  const quantity = (line.quantity ?? line.qty) as number;
  const payload: Record<string, unknown> = {
    lineId,
    productId: getLineProductId(line),
    selectedOptionId: getLineOptionId(line),
    discountAmount: (line.discountAmount as number) ?? 0,
    pieceCount: (line.pieceCount as number | null | undefined) ?? null,
    quantity,
    attributes: normalizeAttributesForLineType(line as unknown as OrderProduct, quantity),
    properties: (line.joinedProperties ?? line.properties) as unknown,
    joinedProperties: line.joinedProperties,
    extraAmount: (line.extraAmount as number) ?? 0,
    isBuyAndGetProduct: line.isBuyAndGetProduct,
    selectedOption: line.selectedOption ?? null,
    name: line.name,
    type: line.type,
    minimumBillableQuantity: line.minimumBillableQuantity ?? 0,
    expressPrice: line.expressPrice,
    price: line.price
  };

  if (Object.prototype.hasOwnProperty.call(line, 'manualUnitPrice')) {
    payload.manualUnitPrice = line.manualUnitPrice;
  }

  if (Object.prototype.hasOwnProperty.call(line, 'assignedEmployee')) {
    payload.assignedEmployee = line.assignedEmployee;
  }

  if (Object.prototype.hasOwnProperty.call(line, 'returnSourceOrderLineId')) {
    payload.returnSourceOrderLineId = line.returnSourceOrderLineId;
  }

  return payload;
}

function formatDeliveryInfoToISO(info: {
  date: Date | string;
  fromTime: string;
  toTime: string;
}): { date: string; fromTime: string; toTime: string } {
  return {
    date: new Date(info.date).toISOString(),
    fromTime: normalizeOrderScheduleTimeValue(String(info.fromTime ?? '')),
    toTime: normalizeOrderScheduleTimeValue(String(info.toTime ?? ''))
  };
}

/**
 * Builds the payload for createOrder (createOrderUsersApp).
 */
export function buildCreateOrderPayload(data: OrderFormValues): CreateOrderPayload {
  const deliveryInfo = formatDeliveryInfoToISO(data.deliveryInfo);
  const deliveryFromTime = deliveryInfo.fromTime || deliveryInfo.toTime || '00:00';

  const payload: CreateOrderPayload = {
    ...data,
    orderType: 'standard',
    createdDate: new Date().toISOString(),
    products: (data.products ?? []).map((p) => {
      const line = mapLineToPayload(p as Record<string, unknown>);
      delete line.returnSourceOrderLineId;
      return line;
    }),
    buyAndGetProducts: (data.buyAndGetProducts ?? []).map((prod: Record<string, unknown>) => ({
      ...mapLineToPayload(prod),
      isFreeItem: prod.isFreeItem
    })),
    deliveryInfo: {
      ...deliveryInfo,
      fromTime: deliveryFromTime
    }
  };

  delete payload.returnSourceOrderId;
  delete payload.returnItemsChargeMode;
  payload.buyAndGetProducts.forEach((line) => delete line.returnSourceOrderLineId);

  if (data.pickup) {
    payload.status = OrderStatuses.PICKUP;
    payload.pickupInfo = formatDeliveryInfoToISO(data.pickupInfo ?? data.deliveryInfo);
  } else {
    payload.status = OrderStatuses.CLEANING;
    payload.pickupInfo = undefined;
  }

  if (data.paymentMethod !== PaymentConfigEnum.DELIVERY) {
    payload.amountPaid = data.total;
  }

  return payload;
}

/**
 * Builds the partial payload for updateOrder (only changed fields, with products/deliveryInfo/pickupInfo formatted for API).
 */
export function buildUpdateOrderPayload(
  currentUpdatingOrder: OrderDTO,
  formData: OrderFormValues
): Partial<OrderDTO> & { _id: string } {
  const newOrderObj = { ...currentUpdatingOrder, ...formData } as Record<string, unknown>;
  const fieldChangedKeys = getOrderUpdateChangedKeys(
    currentUpdatingOrder as unknown as Record<string, unknown>,
    newOrderObj
  );

  const updateOrderObj: Partial<OrderDTO> & { _id: string } = {
    _id: currentUpdatingOrder._id as string
  };

  const trustedProvenanceByLineId = new Map<string, OrderProduct>();
  [
    ...(currentUpdatingOrder.products ?? []),
    ...(currentUpdatingOrder.buyAndGetProducts ?? [])
  ].forEach((line) => {
    if (!line?.returnSourceOrderLineId) return;
    trustedProvenanceByLineId.set(resolveLineId(line), line);
  });
  const mapTrustedUpdateLine = (line: Record<string, unknown>) => {
    const mappedLine = mapLineToPayload(line);
    const trustedPersistedLine = trustedProvenanceByLineId.get(String(mappedLine.lineId ?? ''));
    if (trustedPersistedLine) {
      mappedLine.returnSourceOrderLineId = trustedPersistedLine.returnSourceOrderLineId;
      if (Object.prototype.hasOwnProperty.call(trustedPersistedLine, 'attributes')) {
        mappedLine.attributes = _.cloneDeep(trustedPersistedLine.attributes);
      } else {
        delete mappedLine.attributes;
      }
      // The backend rejects client changes to the stored provenance snapshot.
      // Web POS derives commercial display state again from the SDK on load/edit,
      // so hydrated promotion roles must not be submitted as persisted snapshot state.
      delete mappedLine.isBuyAndGetProduct;
      delete mappedLine.isFreeItem;
      delete mappedLine.discountAmount;
      delete mappedLine.discountAmountMode;
    } else {
      delete mappedLine.returnSourceOrderLineId;
    }
    return mappedLine;
  };

  updateOrderObj.products = (formData.products ?? []).map((prod) =>
    mapTrustedUpdateLine(prod as unknown as Record<string, unknown>) as unknown as OrderProduct
  );
  updateOrderObj.buyAndGetProducts = (
    (formData.buyAndGetProducts ?? []) as Array<Record<string, unknown> & { isFreeItem?: boolean }>
  ).map((prod) => {
    const mappedLine = mapTrustedUpdateLine(prod);
    if (
      !mappedLine.returnSourceOrderLineId &&
      !Object.prototype.hasOwnProperty.call(mappedLine, 'isFreeItem')
    ) {
      mappedLine.isFreeItem = prod.isFreeItem;
    }
    return mappedLine;
  }) as unknown as OrderDTO['buyAndGetProducts'];

  if (currentUpdatingOrder.orderType === 'return') {
    updateOrderObj.orderType = currentUpdatingOrder.orderType;
    updateOrderObj.returnSourceOrderId = currentUpdatingOrder.returnSourceOrderId;
    updateOrderObj.returnItemsChargeMode = currentUpdatingOrder.returnItemsChargeMode;
  }

  for (const orderKey of fieldChangedKeys) {
    if (orderKey === '_id') continue;
    if (orderKey === 'products' || orderKey === 'buyAndGetProducts') continue;
    if (
      orderKey === 'orderType' ||
      orderKey === 'returnSourceOrderId' ||
      orderKey === 'returnItemsChargeMode'
    ) continue;
    const key = orderKey as keyof OrderDTO;
    updateOrderObj[key] = newOrderObj[orderKey] as OrderDTO[keyof OrderDTO];

    if (orderKey === 'deliveryInfo') {
      const deliveryInfo = updateOrderObj.deliveryInfo as unknown as {
        date?: string;
        fromTime?: string;
        toTime?: string;
      };
      if (deliveryInfo) {
        updateOrderObj.deliveryInfo = formatDeliveryInfoToISO({
          date: deliveryInfo.date ?? '',
          fromTime: String(deliveryInfo.fromTime ?? ''),
          toTime: String(deliveryInfo.toTime ?? '')
        }) as unknown as OrderDTO['deliveryInfo'];
      }
    } else if (orderKey === 'pickupInfo') {
      if (updateOrderObj.pickup) {
        const pickupInfo = updateOrderObj.pickupInfo as {
          date?: string;
          fromTime?: string;
          toTime?: string;
        } | undefined;
        if (pickupInfo) {
          updateOrderObj.pickupInfo = formatDeliveryInfoToISO({
            date: pickupInfo.date ?? '',
            fromTime: String(pickupInfo.fromTime ?? ''),
            toTime: String(pickupInfo.toTime ?? '')
          }) as unknown as OrderDTO['pickupInfo'];
        }
      } else {
        updateOrderObj.pickupInfo = undefined;
      }
    }
  }

  return updateOrderObj;
}

export function buildStoreReceptionValidationUpdatePayload(
  currentUpdatingOrder: OrderDTO,
  formData: OrderFormValues
): Partial<OrderDTO> & { _id: string; updateMode: 'store_reception_validation' } {
  const payload = buildUpdateOrderPayload(currentUpdatingOrder, formData) as Partial<OrderDTO> & {
    _id: string;
    updateMode: 'store_reception_validation';
  };

  delete payload.status;
  payload.updateMode = STORE_RECEPTION_VALIDATION_UPDATE_MODE;

  return payload;
}
